import { type RefObject, useEffect } from "react";

export const useActiveTab: (
  targetRefs: RefObject<HTMLDivElement>[],
  callback: (closestIndex: number) => void,
  marginTarget?: number
) => void = (targetRefs, callback, marginTarget = 0) => {
  const farAway = marginTarget * 10 + 200;

  useEffect(() => {
    const onEnterViewport: (entries: IntersectionObserverEntry[]) => void = (
      entries
    ) => {
      if (entries.length > 0 && entries[0].isIntersecting) {
        const positions = targetRefs.map(
          (ref) => ref.current?.getBoundingClientRect().top ?? farAway
        );
        const closestMarginTop = positions.reduce((a, b) =>
          Math.abs(b - marginTarget) < Math.abs(a - marginTarget) ? b : a
        );
        const closestIndex = positions.findIndex((x) => x === closestMarginTop);
        callback(closestIndex);
      }
    };

    const targets: Array<HTMLDivElement | null> = targetRefs.map((_) => null);
    const mainObserver = new IntersectionObserver(onEnterViewport, {
      threshold: [0, 0.2, 0.4, 0.6, 0.8, 1.0],
    });

    for (let i = 0; i < targetRefs.length; i++) {
      const ref = targetRefs[i];
      if (ref.current) {
        targets[i] = ref.current;
        mainObserver.observe(ref.current);
      }
    }

    return () => {
      for (const target of targets) {
        if (target) {
          mainObserver.unobserve(target);
        }
      }
    };
  }, [callback, targetRefs, marginTarget, farAway]);
};
